import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, DatePicker, message } from 'antd';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    ReportFilter, 
    SalesKPISummary, 
    ReceiptItem 
} from '../../model/report';
import { fetchSalesReport } from '../../api/report';
import { getDateRangeForPreset, DatePresetType } from '../../utils/date-filter-utils';
import ReportVisualAnalytics from './report-visual-analytics';
import ReportSalesSummary from './report-sales-summary';
import ReportProducts from './report-products';
import ReportInventory from './report-inventory';
import ReportWorkShift from './report-work-shift';
import ReportMember from './report-member';
import { printDocument } from '../../utils/print-utils';
import { printVisualAnalyticsReport } from '../../utils/record-print-utils';
import '../../assets/css/report.css';

const ReportDashboard: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab') || 'analytics';
    const [activeTab, setActiveTab] = useState<string>(tabParam);
    const reportContentRef = useRef<HTMLDivElement>(null);

    // Sync tab with URL search parameter
    useEffect(() => {
        const t = searchParams.get('tab');
        if (t && ['analytics', 'sales', 'receipts', 'products', 'inventory', 'shifts', 'members'].includes(t)) {
            setActiveTab(t === 'receipts' ? 'sales' : t);
        }
    }, [searchParams]);

    // Filter state
    const [filter, setFilter] = useState<ReportFilter>({
        preset: 'today',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });

    // Sales Data state
    const [kpis, setKpis] = useState<SalesKPISummary>({
        totalSales: 0,
        totalBills: 0,
        avgBillAmount: 0,
        totalDiscount: 0,
        totalVat: 0,
        totalItemsSold: 0,
        totalProfit: 0,
        cashSales: 0,
        transferSales: 0,
        creditSales: 0
    });
    const [receipts, setReceipts] = useState<ReceiptItem[]>([]);
    const [dailyTrend, setDailyTrend] = useState<{ date: string; sales: number; bills: number }[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    // Load sales data when filter changes
    const loadSalesData = async () => {
        setLoading(true);
        try {
            const data = await fetchSalesReport(filter);
            setKpis(data.kpis);
            setReceipts(data.receipts || []);
            setDailyTrend(data.dailyTrend || []);
        } catch (e: any) {
            // A failed load is shown, not hidden behind stale or invented
            // figures. The 401 case is handled by the auth interceptor.
            console.error(e);
            setReceipts([]);
            setDailyTrend([]);
            message.error(e?.response?.data?.message || e?.message || 'โหลดรายงานไม่สำเร็จ');
        }
        setLoading(false);
    };

    useEffect(() => {
        loadSalesData();
    }, [filter]);

    // Handle Quick Date Presets
    const handlePresetChange = (preset: ReportFilter['preset']) => {
        const { start, end } = getDateRangeForPreset(preset as DatePresetType);
        setFilter(prev => ({
            ...prev,
            preset,
            startDate: start,
            endDate: end
        }));
    };

    const handleTabChange = (key: string) => {
        setActiveTab(key);
        setSearchParams({ tab: key });
    };

    const handlePrintCurrentReport = () => {
        if (activeTab === 'analytics') {
            printVisualAnalyticsReport({
                viewMode: 'weekly',
                totalSales: 39950,
                growthPercent: 12.5,
                totalBills: 136,
                avgPerBill: 294,
                peakText: 'วันเสาร์ (฿8,900)',
                data: [
                    { label: 'จันทร์', value: 4250, compareValue: 3800, bills: 14 },
                    { label: 'อังคาร', value: 3900, compareValue: 4100, bills: 12 },
                    { label: 'พุธ', value: 4800, compareValue: 4300, bills: 16 },
                    { label: 'พฤหัส', value: 5200, compareValue: 4600, bills: 18 },
                    { label: 'ศุกร์', value: 6800, compareValue: 5900, bills: 22 },
                    { label: 'เสาร์', value: 8900, compareValue: 7800, bills: 28 },
                    { label: 'อาทิตย์', value: 8100, compareValue: 7200, bills: 26 }
                ],
                paymentDonutData: [
                    { label: 'สแกน QR / เงินโอน', value: 24500, color: '#3B82F6', percentage: 58 },
                    { label: 'เงินสด (Cash)', value: 13200, color: '#10B981', percentage: 31 },
                    { label: 'บัตรเครดิต', value: 4250, color: '#8B5CF6', percentage: 11 }
                ],
                categoryDonutData: [
                    { label: 'เครื่องดื่ม & กาแฟ', value: 22800, color: '#FF7A59', percentage: 54 },
                    { label: 'เบเกอรี่ & ขนมอบ', value: 12400, color: '#F59E0B', percentage: 30 },
                    { label: 'อาหารว่าง & อื่นๆ', value: 6750, color: '#EC4899', percentage: 16 }
                ],
                hourlyData: [
                    { hour: '08:00', sales: 1850, bills: 6 },
                    { hour: '09:00', sales: 2900, bills: 9 },
                    { hour: '10:00', sales: 3400, bills: 11 },
                    { hour: '11:00', sales: 4800, bills: 15 },
                    { hour: '12:00', sales: 6200, bills: 19 },
                    { hour: '13:00', sales: 4900, bills: 16 },
                    { hour: '14:00', sales: 3100, bills: 10 },
                    { hour: '15:00', sales: 2800, bills: 9 },
                    { hour: '16:00', sales: 3600, bills: 12 },
                    { hour: '17:00', sales: 4500, bills: 14 },
                    { hour: '18:00', sales: 3800, bills: 12 },
                    { hour: '19:00', sales: 2100, bills: 7 },
                    { hour: '20:00', sales: 1100, bills: 4 }
                ]
            });
        } else {
            const activePane = reportContentRef.current?.querySelector('.ant-tabs-tabpane-active') as HTMLElement;
            printDocument(activePane || reportContentRef.current, {
                documentType: 'a4-landscape',
                title: `รายงานระบบ POS (${activeTab})`
            });
        }
    };

    const tabItems = [
        {
            key: 'analytics',
            label: (
                <span>
                    <FontAwesomeIcon icon="chart-pie" style={{ marginRight: '6px' }} />
                    ภาพรวมสถิติ
                </span>
            ),
            children: <ReportVisualAnalytics />
        },
        {
            key: 'sales',
            label: (
                <span>
                    <FontAwesomeIcon icon="dollar-sign" style={{ marginRight: '6px' }} />
                    สรุปยอดขาย
                </span>
            ),
            children: (
                <ReportSalesSummary
                    kpis={kpis}
                    receipts={receipts}
                    dailyTrend={dailyTrend}
                    onRefresh={loadSalesData}
                />
            )
        },
        {
            key: 'products',
            label: (
                <span>
                    <FontAwesomeIcon icon="fire" style={{ marginRight: '6px' }} />
                    สินค้าขายดี
                </span>
            ),
            children: <ReportProducts filter={filter} />
        },
        {
            key: 'inventory',
            label: (
                <span>
                    <FontAwesomeIcon icon="boxes-stacked" style={{ marginRight: '6px' }} />
                    สินค้าคงเหลือ
                </span>
            ),
            children: <ReportInventory filter={filter} />
        },
        {
            key: 'shifts',
            label: (
                <span>
                    <FontAwesomeIcon icon="user-clock" style={{ marginRight: '6px' }} />
                    กะการทำงาน
                </span>
            ),
            children: <ReportWorkShift filter={filter} />
        },
        {
            key: 'members',
            label: (
                <span>
                    <FontAwesomeIcon icon="users" style={{ marginRight: '6px' }} />
                    สมาชิก
                </span>
            ),
            children: <ReportMember filter={filter} />
        }
    ];

    return (
        <div className="report-dashboard-container">
            {/* Top Page Header */}
            <div className="report-header-row">
                <div className="report-title-box">
                    <div className="report-title-icon">
                        <FontAwesomeIcon icon="chart-pie" />
                    </div>
                    <div>
                        <div className="report-title-text">ศูนย์รวมรายงานและสถิติ (Reports & Analytics)</div>
                        <div className="report-subtitle">
                            ติดตามผลการดำเนินงาน ยอดขาย สินค้า และสต๊อกของร้านค้าแบบเรียลไทม์
                        </div>
                    </div>
                </div>

                <div className="report-header-actions">
                    <button
                        type="button"
                        className="btn-report-action btn-refresh"
                        onClick={loadSalesData}
                        title="รีเฟรชข้อมูลล่าสุด"
                    >
                        <FontAwesomeIcon icon="rotate-right" spin={loading} />
                        <span>รีเฟรช</span>
                    </button>
                    <button
                        type="button"
                        className="btn-report-action btn-print"
                        onClick={handlePrintCurrentReport}
                        title="พิมพ์หน้ารายงานนี้"
                    >
                        <FontAwesomeIcon icon="print" />
                        <span>พิมพ์รายงาน</span>
                    </button>
                </div>
            </div>

            {/* Smart Date Filter Toolbar (Only show on tabs other than 'analytics') */}
            {activeTab !== 'analytics' && (
                <div className="report-filter-bar">
                    <div className="filter-presets-group">
                        <button
                            type="button"
                            className={`filter-btn ${filter.preset === 'all' ? 'active' : ''}`}
                            onClick={() => handlePresetChange('all')}
                        >
                            ทั้งหมด
                        </button>
                        <button
                            type="button"
                            className={`filter-btn ${filter.preset === 'today' ? 'active' : ''}`}
                            onClick={() => handlePresetChange('today')}
                        >
                            วันนี้
                        </button>
                        <button
                            type="button"
                            className={`filter-btn ${filter.preset === 'yesterday' ? 'active' : ''}`}
                            onClick={() => handlePresetChange('yesterday')}
                        >
                            เมื่อวาน
                        </button>
                        <button
                            type="button"
                            className={`filter-btn ${filter.preset === 'last7days' ? 'active' : ''}`}
                            onClick={() => handlePresetChange('last7days')}
                        >
                            7 วันที่แล้ว
                        </button>
                        <button
                            type="button"
                            className={`filter-btn ${filter.preset === 'this_week' ? 'active' : ''}`}
                            onClick={() => handlePresetChange('this_week')}
                        >
                            สัปดาห์นี้
                        </button>
                        <button
                            type="button"
                            className={`filter-btn ${filter.preset === 'this_month' ? 'active' : ''}`}
                            onClick={() => handlePresetChange('this_month')}
                        >
                            เดือนนี้
                        </button>
                        <button
                            type="button"
                            className={`filter-btn ${filter.preset === 'last_month' ? 'active' : ''}`}
                            onClick={() => handlePresetChange('last_month')}
                        >
                            เดือนที่แล้ว
                        </button>
                    </div>

                    <div className="filter-custom-date" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span className="filter-date-label">📅 เลือกช่วงวันที่:</span>
                        <DatePicker
                            placeholder="จากวันที่"
                            format="DD/MM/YYYY"
                            value={filter.startDate ? dayjs(filter.startDate) : null}
                            onChange={(d) => {
                                const startStr = d ? d.format('YYYY-MM-DD') : '';
                                setFilter(prev => ({
                                    ...prev,
                                    preset: 'custom',
                                    startDate: startStr
                                }));
                            }}
                            style={{ borderRadius: '10px', height: '36px' }}
                        />
                        <span style={{ color: '#94A3B8', fontWeight: 600 }}>ถึง</span>
                        <DatePicker
                            placeholder="ถึงวันที่"
                            format="DD/MM/YYYY"
                            value={filter.endDate ? dayjs(filter.endDate) : null}
                            onChange={(d) => {
                                const endStr = d ? d.format('YYYY-MM-DD') : '';
                                setFilter(prev => ({
                                    ...prev,
                                    preset: 'custom',
                                    endDate: endStr
                                }));
                            }}
                            style={{ borderRadius: '10px', height: '36px' }}
                        />
                    </div>
                </div>
            )}

            {/* Main Tabs Navigation */}
            <div className="report-tabs-wrapper" ref={reportContentRef}>
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    items={tabItems}
                    type="line"
                    size="middle"
                />
            </div>
        </div>
    );
};

export default ReportDashboard;
